import { supabase } from '../../lib/supabase';
import type { Faq } from '../types';
import { throwWithContext } from './_shared';

/**
 * Fetches all FAQs, ordered by category and display_order.
 */
export async function listAllFaqs(): Promise<Faq[]> {
  const { data, error } = await supabase
    .from('faqs')
    .select('*')
    .order('category', { ascending: true })
    .order('display_order', { ascending: true });

  if (error) {
    throwWithContext('Failed to fetch FAQs', error);
  }

  // The types from supabase might need an `as unknown as Faq[]` since 
  // Database type might not be fully mapped to the custom types yet, but
  // assuming database.types.ts has the right shape for JSONB data.
  return (data || []) as unknown as Faq[];
}

/**
 * Fetches a single FAQ by its ID (slug).
 */
export async function getFaqById(id: string): Promise<Faq | null> {
  const { data, error } = await supabase
    .from('faqs')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // PostgREST code for zero rows
    throwWithContext('Failed to fetch FAQ', error);
  }

  return data as unknown as Faq;
}

/**
 * Fetches related FAQs given an array of IDs.
 */
export async function getRelatedFaqs(ids: string[]): Promise<Faq[]> {
  if (!ids || ids.length === 0) return [];

  const { data, error } = await supabase
    .from('faqs')
    .select('*')
    .in('id', ids)
    .order('display_order', { ascending: true });

  if (error) {
    throwWithContext('Failed to fetch related FAQs', error);
  }

  // Return exactly in the order requested, or let the display_order handle it.
  // The caller can sort if specific order is needed, but DB returns them based on default/order clause.
  return (data || []) as unknown as Faq[];
}
